import styled, { css } from 'styled-components';
import { Button, Col, Collapse, Radio, Row } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import ButtonDefault from '@/components/ui/ButtonDefault';
import { Risk } from '@/components/views/Resolution886/enums';

export const DefaultStepTitle = styled.h3`
	font-size: ${({ theme }) => theme.sizes.fonts.fs_2xl};
	line-height: ${({ theme }) => theme.sizes.line_heights.lh_lg};
	font-weight: ${({ theme }) => theme.sizes.weights.semibold};
	color: ${({ theme }) => theme.colors.text};
`;

export const DefaultStepHeading = styled.h2`
	text-align: center;
	${({ theme }) => css`
		${{ ...theme.fonts.display.sm }}
	`}
`;

export const DefaultStepSubTitle = styled.h4`
	font-size: ${({ theme }) => theme.sizes.fonts.fs_md};
	line-height: ${({ theme }) => theme.sizes.line_heights.lh_md};
	letter-spacing: ${({ theme }) => theme.sizes.letter_spacings.ls_lg};
	font-weight: ${({ theme }) => theme.sizes.weights.regular};
	color: ${({ theme }) => theme.colors.text};
`;

export const DefaultRowContent = styled(Row)`
	background-color: ${({ theme }) => theme.palette.secondary[50]};
	border-radius: 20px;
	padding-top: 40px;
	padding-bottom: 40px;
	padding-left: 20px;
	padding-right: 20px;
`;

export const DefaultResultWrapper = styled(Row)`
	gap: 16px;
	opacity: 1;
	width: 100%;
	height: 100%;
	display: flex;
	padding: 40px 60px;
	border-radius: 20px;
	flex-direction: column;
	background-color: ${({ theme }) => theme.palette.secondary[50]};
`;

export const DefaultResultContent = styled(Row)`
	gap: 48px;
	display: flex;
	padding: 40px;
	border-radius: 24px;
	flex-direction: column;
	background-color: ${({ theme }) => theme.palette.secondary.white};
`;

export const FlexSpaceBetween = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

export const CustomRadioGroup = styled(Radio.Group)`
	display: flex;
	gap: 16px;

	.ant-radio-button-wrapper {
		width: 100%;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: ${({ theme }) => theme.border.border_radius.br_sm};
		border: 1px solid ${({ theme }) => theme.palette.secondary[150]};

		span {
			${({ theme }) => css`
				${{ ...theme.fonts.label.md }}
			`}
			font-weight: ${({ theme }) => theme.sizes.weights.regular};
		}

		&::before {
			display: none;
		}
	}

	.ant-radio-button-wrapper-checked {
		&.radio-tolerable {
			background-color: ${({ theme }) => theme.colors.risk.low};
			border-color: ${({ theme }) => theme.colors.risk.low};
			color: ${({ theme }) => theme.palette.secondary.white};

			span {
				font-weight: ${({ theme }) => theme.sizes.weights.semibold} !important;
			}
		}

		&.radio-moderate {
			background-color: ${({ theme }) => theme.colors.risk.medium};
			border-color: ${({ theme }) => theme.colors.risk.medium};
			color: ${({ theme }) => theme.palette.secondary.white};

			span {
				font-weight: ${({ theme }) => theme.sizes.weights.semibold} !important;
			}
		}

		&.radio-untolerable {
			background-color: ${({ theme }) => theme.colors.risk.high};
			border-color: ${({ theme }) => theme.colors.risk.high};
			color: ${({ theme }) => theme.palette.secondary.white};

			span {
				font-weight: ${({ theme }) => theme.sizes.weights.semibold} !important;
			}
		}
	}
`;

export const CustomLabel = styled.label`
	font-size: ${({ theme }) => theme.fonts.label.md.fontSize} !important;
	line-height: ${({ theme }) => theme.fonts.label.md.lineHeight} !important;
	font-weight: ${({ theme }) => theme.fonts.label.md.fontWeight} !important;
	letter-spacing: ${({ theme }) => theme.fonts.label.md.letterSpacing} !important;
	color: ${({ theme }) => theme.colors.text} !important;
	margin-bottom: 16px;
	display: block;
`;

export const CustomButton = styled(Button)`
	align-items: center;
	border-radius: 12px;
	display: flex;
	height: 80px;
	justify-content: space-between;
	width: 100%;
	padding: 0 20px;

	&:hover,
	&:focus {
		h4,
		p {
			color: ${({ theme }) => theme.colors.text};
		}
	}
`;

export const CustomContainerTooltip = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	height: 100%;

	.content-tool {
		text-align: left;
		margin-left: 20px;

		h4 {
			${({ theme }) => css`
				${{ ...theme.fonts.label.md }}
			`}
		}

		p {
			${({ theme }) => css`
				${{ ...theme.fonts.body.sm }}
			`}
			margin: 0;
		}
	}
`;

export const CategoryButton = styled(Button)`
	align-items: center;
	border-radius: ${({ theme }) => theme.border.border_radius.br_xl};
	display: flex;
	justify-content: space-between;
	padding: 16px 20px;
	height: auto;
	width: 100%;

	p {
		${({ theme }) => css`
			${{ ...theme.fonts.label.md }}
		`}
		font-weight: ${({ theme }) => theme.sizes.weights.semibold};
		line-height: ${({ theme }) => theme.sizes.line_heights.lh_md};
		margin: 0;
	}
`;

export const StatusContent = styled.div<{ risk?: Risk }>`
	background-color: ${({ theme, risk }) =>
		risk === Risk.TOLERABLE
			? theme.colors.risk.low
			: risk === Risk.MODERATE
				? theme.colors.risk.medium
				: risk === Risk.NOT_TOLERABLE
					? theme.colors.risk.high
					: theme.palette.secondary[100]};

	border-radius: 4px;
	height: 32px;
	padding: 4px 8px;
	width: fit-content;

	p {
		color: ${({ theme, risk }) => (risk ? theme.palette.secondary.white : theme.colors.text)} !important;
		font-size: 14px !important;
		font-weight: ${({ theme, risk }) =>
			risk ? theme.sizes.weights.semibold : theme.sizes.weights.regular} !important;
		margin: 0 !important;
	}
`;

export const CustomActionPlanCard = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	grid-template-rows: 1fr;
	grid-column-gap: 0px;
	grid-row-gap: 0px;
	background-color: ${({ theme }) => theme.palette.secondary[100]};
	border-radius: ${({ theme }) => theme.border.border_radius.br_lg};
	padding: 16px 20px;
	width: 100%;

	h4,
	p {
		margin: 0;
	}

	h4 {
		${({ theme }) => css`
			${{ ...theme.fonts.label.lg }}
		`}
		font-weight: ${({ theme }) => theme.sizes.weights.semibold};
	}

	p {
		${({ theme }) => css`
			${{ ...theme.fonts.body.lg }}
		`}
		text-align: center;
	}

	.actions {
		align-items: center;
		display: flex;
		justify-content: flex-end;
		gap: 10px;
	}
`;

export const HeadingSmall = styled.h3`
	${({ theme }) => css`
		${{ ...theme.fonts.heading.sm }}
	`}
`;

export const CustomColBorder = styled(Col)`
	border-top: 1px solid ${({ theme }) => theme.palette.secondary[150]};
	padding-top: 40px;
	margin-top: 10px;
`;

export const CustomCollapse = styled(Collapse)`
	margin-top: 10px;

	.ant-collapse-header {
		display: none !important;
	}

	.ant-collapse-content {
		background-color: ${({ theme }) => theme.palette.secondary.white} !important;
		border-radius: ${({ theme }) => theme.border.border_radius.br_lg} !important;
		border: 1px solid ${({ theme }) => theme.palette.secondary[200]} !important;

		.ant-collapse-content-box {
			padding: 24px !important;

			.ant-col {
				display: flex;
				align-items: center;
				justify-content: space-between;

				p {
					${({ theme }) => css`
						${{ ...theme.fonts.label.md }}
					`}
					font-weight: ${({ theme }) => theme.sizes.weights.semibold};
					line-height: ${({ theme }) => theme.sizes.line_heights.lh_md};
					margin: 5px 0;
				}
			}
		}
	}
`;

export const StatusContentCollapse = styled.div`
	background-color: ${({ theme }) => theme.palette.secondary[100]};
	border-radius: 4px;
	height: 32px;
	padding: 4px 16px;
	line-height: 32px;
	width: fit-content;

	p {
		color: ${({ theme }) => theme.colors.text} !important;
		font-size: 14px !important;
		font-weight: ${({ theme }) => theme.sizes.weights.semibold} !important;
		margin: 0 !important;
	}
`;

export const CustomButtonDefault = styled(ButtonDefault)`
	&.active {
		background-color: ${({ theme }) => theme.palette.primary[100]} !important;
		border-color: ${({ theme }) => theme.palette.primary[100]} !important;
		color: ${({ theme }) => theme.palette.primary.default} !important;
	}
`;

export const CustomToast = styled.div`
	left: 50%;
	gap: 12px;
	top: 20px;
	z-index: 1000;
	display: flex;
	position: fixed;
	padding: 16px 20px;
	align-items: center;
	transform: translateX(-50%);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	background: ${({ theme }) => theme.colors.risk.high};
	border-radius: ${({ theme }) => theme.border.border_radius.br_lg};
`;

export const WarningIcon = styled.div`
	display: flex;
	align-items: center;
	color: ${({ theme }) => theme.palette.secondary.white};

	svg {
		color: ${({ theme }) => theme.palette.secondary.white};
	}
`;

export const ToastMessage = styled.span`
	color: ${({ theme }) => theme.palette.secondary.white};
	font-size: ${({ theme }) => theme.sizes.fonts.fs_sm};
	line-height: ${({ theme }) => theme.sizes.line_heights.lh_md};
	letter-spacing: ${({ theme }) => theme.sizes.letter_spacings.ls_lg};
	font-weight: ${({ theme }) => theme.sizes.weights.semibold};
`;

export const CustomText = styled.p`
	font-size: ${({ theme }) => theme.sizes.fonts.fs_sm} !important;
	line-height: ${({ theme }) => theme.sizes.line_heights.lh_md} !important;
	letter-spacing: ${({ theme }) => theme.sizes.letter_spacings.ls_lg} !important;
	font-weight: ${({ theme }) => theme.sizes.weights.regular} !important;
`;

export const TableWrapper = styled.div`
	width: 100%;
	overflow: hidden;
`;

export const CustomTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	border-spacing: 0;
	overflow: hidden;
	border-radius: 8px;

	thead {
		background-color: ${({ theme }) => theme.palette.secondary[100]};

		th {
			font-size: ${({ theme }) => theme.sizes.fonts.fs_sm};
			font-weight: ${({ theme }) => theme.sizes.weights.semibold} !important;
		}
	}

	tbody {
		background-color: ${({ theme }) => theme.palette.secondary[50]};

		td {
			font-size: ${({ theme }) => theme.sizes.fonts.fs_xs};
		}
	}

	th,
	td {
		padding: 12px;

		span {
			font-weight: ${({ theme }) => theme.sizes.weights.semibold};
		}
	}
`;

export const CustomRowPadding = styled(Row)`
	padding: 30px 0;
`;

export const CustomColFlex = styled(Col)`
	display: flex;
	justify-content: center;
	gap: 16px;
`;

export const CustomDivMargin = styled.div<{ margin?: string }>`
	margin: ${({ margin }) => margin || '25px 0'};
`;

export const CustomColMargin = styled(Col)<{ margin?: string }>`
	margin: ${({ margin }) => margin || '25px 0'};
`;

export const CustomColFlexStart = styled(Col)`
	align-items: start !important;
	margin-bottom: 20px !important;
	flex-direction: column !important;
	justify-content: flex-start !important;
`;

export const TooltipIcon = styled(InfoCircleOutlined)`
	font-size: 13px;
	margin-left: 8px;
	vertical-align: top;
	transform: translateY(-1.33px);
`;
